import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import AdminSidebar, { AdminTab } from '../../components/sidebar/AdminSidebar';
import { RootState } from '../../redux/store';
import { User, Admin, AIAgent, UsageStatistic } from '../../types';
import {
  fetchUsers,
  fetchAgents,
  fetchAdminStats,
  fetchOrganizations,
  fetchImageStyles,
  fetchVideoTemplates,
  createUser,
  updateUser,
  deleteUser,
  createAgent,
  updateAgent,
  deleteAgent,
  createOrganization,
  updateOrganization,
  deleteOrganization,
  toggleOrganizationActive,
  createImageStyle,
  updateImageStyle,
  deleteImageStyle,
  toggleImageStyle,
  createVideoTemplate,
  updateVideoTemplate,
  deleteVideoTemplate,
  toggleVideoTemplate,
  setSelectedUser,
  setSelectedAgent,
  setSelectedOrganization,
  setSelectedImageStyle,
  setSelectedVideoTemplate
} from '../../redux/admin/adminSlice';
import type { AdminFirebaseCreationInfo } from '../../redux/admin/adminSlice';
import { Organization } from '../../services/api/adminOrganizationService';
import adminImageStyleService, { AdminImageStyle, ImageStyleCreateRequest, ImageStyleUpdateRequest, ImageStyleCategory } from '../../services/api/adminImageStyleService';
import { AdminVideoTemplate, VideoTemplateCreateRequest, VideoTemplateUpdateRequest } from '../../services/api/adminVideoTemplateService';
import AIAgentFormModal from '../../components/admin/AIAgentFormModal';
import UserFormModal from '../../components/admin/UserFormModal';
import OrganizationFormModal from '../../components/admin/OrganizationFormModal';
import ImageStyleFormModal from '../../components/admin/ImageStyleFormModal';
import CategoryList from '../../components/admin/CategoryList';
import ImageStyleCategoryList from '../../components/admin/ImageStyleCategoryList';
import MaterialStyleList from '../../components/admin/MaterialStyleList';
import MaterialStyleCategoryList from '../../components/admin/MaterialStyleCategoryList';
import ImagePromptTemplateList from '../../components/admin/ImagePromptTemplateList';
import BannerPromptWeeklyReports from '../../components/admin/BannerPromptWeeklyReports';
import MagicResizePresetManager from '../../components/admin/MagicResizePresetManager';
import MagicResizeDebugPage from '../../components/admin/MagicResizeDebugPage';
import MangaStyleManagement from '../../components/admin/MangaStyleManagement';
import MusicStyleManagement from '../../components/admin/MusicStyleManagement';
import CarouselStyleManagement from '../../components/admin/CarouselStyleManagement';
import SamplePromptList from '../../components/admin/SamplePromptList';
import VideoTemplateList from '../../components/admin/VideoTemplateList';
import VideoTemplateFormModal from '../../components/admin/VideoTemplateFormModal';
import UsageOverviewDashboard from '../../components/admin/usage/UsageOverviewDashboard';
import NewsManagementSection from '../../components/admin/NewsManagementSection';
import HelpArticleManagementSection from '../../components/admin/HelpArticleManagementSection';
import ExternalApiTokenList from '../../components/admin/ExternalApiTokenList';
import BigQueryHandoffPanel from '../../components/admin/BigQueryHandoffPanel';
import AiAnalysisModelPanel from '../../components/admin/AiAnalysisModelPanel';
import StyleQualityPanel from '../../components/admin/StyleQualityPanel';
import TutorialSettingsPanel from '../../components/admin/TutorialSettingsPanel';
import UgcPresetPanel from '../../components/admin/UgcPresetPanel';
import VideoStyleRecipePanel from '../../components/admin/VideoStyleRecipePanel';
import AdAccountOverviewPanel from '../../components/admin/AdAccountOverviewPanel';
import BehaviorAnalyticsPanel from '../../components/admin/behavior/BehaviorAnalyticsPanel';
import PartnerPortalManagement from '../../components/admin/PartnerPortalManagement';
import CampaignManagementPanel from '../../components/admin/CampaignManagementPanel';
import {
  ResponsiveAppShell,
  TaskyAlert,
  TaskyBadge,
  TaskyButton,
  TaskyEmptyState,
  TaskyIconButton,
  TaskyInput,
  TaskyLabel,
  TaskySpinner,
  TaskyStatCard,
  TaskyTable,
  TaskyTableBody,
  TaskyTableCell,
  TaskyTableHead,
  TaskyTableHeaderCell,
  TaskyTableRow,
  useConfirm,
} from '../../components/tasky';
import { useToast } from '../../contexts/ToastContext';
import { formatLimit } from '../../utils/limitDisplay';
import {
  Bot,
  Building2,
  Download,
  Edit,
  MessageSquare,
  MessagesSquare,
  Palette,
  Plus,
  Power,
  ShieldCheck,
  Trash2,
  UserCheck,
  Users,
} from 'lucide-react';

// AdminTab型はAdminSidebarからインポート済み

// URL slug ↔ AdminTab マッピング
const slugToTab: Record<string, AdminTab> = {
  users: 'users',
  agents: 'agents',
  'ai-analysis-models': 'aiAnalysisModels',
  'ad-accounts': 'adAccounts',
  organizations: 'organizations',
  categories: 'categories',
  'image-styles': 'imageStyles',
  'style-quality': 'styleQuality',
  'style-categories': 'imageStyleCategories',
  'material-styles': 'materialStyles',
  'material-style-categories': 'materialStyleCategories',
  'carousel-styles': 'carouselStyles',
  'prompt-templates': 'promptTemplates',
  'banner-prompt-reports': 'bannerPromptReports',
  'sample-prompts': 'samplePrompts',
  'manga-styles': 'mangaStyles',
  'music-styles': 'musicStyles',
  'video-templates': 'videoTemplates',
  'ugc-presets': 'ugcPresets',
  'video-style-recipes': 'videoStyleRecipes',
  'resize-presets': 'magicResizePresets',
  'resize-debug': 'magicResizeDebug',
  statistics: 'statistics',
  news: 'news',
  'help-articles': 'helpArticles',
  tutorials: 'tutorials',
  'external-api-tokens': 'externalApiTokens',
  'bigquery-handoff': 'bigQueryHandoff',
  'partner-portal': 'partnerPortal',
  behavior: 'behavior',
  campaigns: 'campaigns',
};

const tabToSlug: Record<AdminTab, string> = {
  users: 'users',
  agents: 'agents',
  aiAnalysisModels: 'ai-analysis-models',
  adAccounts: 'ad-accounts',
  organizations: 'organizations',
  categories: 'categories',
  imageStyles: 'image-styles',
  styleQuality: 'style-quality',
  imageStyleCategories: 'style-categories',
  materialStyles: 'material-styles',
  materialStyleCategories: 'material-style-categories',
  carouselStyles: 'carousel-styles',
  promptTemplates: 'prompt-templates',
  bannerPromptReports: 'banner-prompt-reports',
  samplePrompts: 'sample-prompts',
  mangaStyles: 'manga-styles',
  musicStyles: 'music-styles',
  videoTemplates: 'video-templates',
  ugcPresets: 'ugc-presets',
  videoStyleRecipes: 'video-style-recipes',
  magicResizePresets: 'resize-presets',
  magicResizeDebug: 'resize-debug',
  statistics: 'statistics',
  news: 'news',
  helpArticles: 'help-articles',
  tutorials: 'tutorials',
  externalApiTokens: 'external-api-tokens',
  bigQueryHandoff: 'bigquery-handoff',
  partnerPortal: 'partner-portal',
  behavior: 'behavior',
  campaigns: 'campaigns',
};

// 横タブバー表示用の定義（既存のタブ順序・ラベルを維持）
const ADMIN_TAB_ITEMS: { key: AdminTab; label: string }[] = [
  { key: 'users', label: 'ユーザー管理' },
  { key: 'agents', label: 'AIエージェント管理' },
  { key: 'aiAnalysisModels', label: 'AI解析モデル設定' },
  { key: 'adAccounts', label: '広告アカウント' },
  { key: 'organizations', label: '組織管理' },
  { key: 'categories', label: 'カテゴリ管理' },
  { key: 'imageStyles', label: '画像スタイル管理' },
  { key: 'styleQuality', label: 'スタイル品質' },
  { key: 'imageStyleCategories', label: 'スタイルカテゴリ管理' },
  { key: 'materialStyles', label: '素材スタイル管理' },
  { key: 'materialStyleCategories', label: '素材カテゴリ管理' },
  { key: 'carouselStyles', label: 'カルーセルスタイル管理' },
  { key: 'promptTemplates', label: 'プロンプト管理' },
  { key: 'bannerPromptReports', label: 'バナープロンプト分析' },
  { key: 'mangaStyles', label: '漫画スタイル管理' },
  { key: 'musicStyles', label: '音楽スタイル管理' },
  { key: 'videoTemplates', label: '動画テンプレート管理' },
  { key: 'ugcPresets', label: 'UGC 出演者・場所' },
  { key: 'videoStyleRecipes', label: '動画スタイル' },
  { key: 'magicResizePresets', label: 'リサイズプリセット管理' },
  { key: 'magicResizeDebug', label: 'リサイズデバッグ' },
  { key: 'news', label: 'ニュース管理' },
  { key: 'helpArticles', label: 'ヘルプ記事管理' },
  { key: 'tutorials', label: 'チュートリアル' },
  { key: 'externalApiTokens', label: '外部連携API' },
  { key: 'bigQueryHandoff', label: 'BI連携' },
  { key: 'partnerPortal', label: 'パートナー管理' },
  { key: 'campaigns', label: 'キャンペーン' },
  { key: 'behavior', label: '行動分析' },
  { key: 'statistics', label: '使用統計' },
];

const AdminPage: React.FC = () => {
  const { showToast } = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { tab } = useParams<{ tab: string }>();

  // Redux状態
  const { user } = useSelector((state: RootState) => state.auth);
  const {
    users,
    agents,
    stats,
    organizations,
    imageStyles,
    videoTemplates,
    selectedUser,
    selectedAgent,
    selectedOrganization,
    selectedImageStyle,
    selectedVideoTemplate,
    isLoading,
    error
  } = useSelector((state: RootState) => state.admin);

  // URLパラメータからアクティブタブを導出
  const activeTab: AdminTab = (tab && slugToTab[tab]) || 'users';

  // タブ変更時にURLを更新
  const handleTabChange = (newTab: AdminTab) => {
    navigate(`/admin/${tabToSlug[newTab]}`);
  };
  const [isDownloading, setIsDownloading] = useState(false);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  
  // モーダル関連の状態
  const [isAgentModalOpen, setIsAgentModalOpen] = useState(false);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isOrganizationModalOpen, setIsOrganizationModalOpen] = useState(false);
  const [isImageStyleModalOpen, setIsImageStyleModalOpen] = useState(false);
  const [isVideoTemplateModalOpen, setIsVideoTemplateModalOpen] = useState(false);

  // 画像スタイルカテゴリの状態
  const [imageStyleCategories, setImageStyleCategories] = useState<ImageStyleCategory[]>([]);

  // 管理者権限チェックはAdminRouteで実施済みのため、ここでは不要

  // 初期データ取得
  useEffect(() => {
    dispatch(fetchUsers());
    dispatch(fetchAgents());
    dispatch(fetchAdminStats());
    dispatch(fetchOrganizations());
    dispatch(fetchImageStyles());
    dispatch(fetchVideoTemplates());
  }, [dispatch]);

  // 画像スタイルカテゴリ取得
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categories = await adminImageStyleService.getActiveCategories();
        setImageStyleCategories(categories);
      } catch (error) {
        console.error('Failed to fetch image style categories:', error);
      }
    };
    fetchCategories();
  }, []);

  // モーダルを開く関数
  const openAgentModal = (agent?: AIAgent) => {
    if (agent) {
      dispatch(setSelectedAgent(agent));
    } else {
      dispatch(setSelectedAgent(null));
    }
    setIsAgentModalOpen(true);
  };

  // ユーザーモーダルを開く関数
  const openUserModal = (user?: User) => {
    if (user) {
      dispatch(setSelectedUser(user));
    } else {
      dispatch(setSelectedUser(null));
    }
    setIsUserModalOpen(true);
  };

  // モーダルを閉じる関数
  const closeAgentModal = () => {
    setIsAgentModalOpen(false);
    dispatch(setSelectedAgent(null));
  };

  // ユーザーモーダルを閉じる関数
  const closeUserModal = () => {
    setIsUserModalOpen(false);
    dispatch(setSelectedUser(null));
  };

  // 組織モーダルを開く関数
  const openOrganizationModal = (organization?: Organization) => {
    if (organization) {
      dispatch(setSelectedOrganization(organization));
    } else {
      dispatch(setSelectedOrganization(null));
    }
    setIsOrganizationModalOpen(true);
  };

  // 組織モーダルを閉じる関数
  const closeOrganizationModal = () => {
    setIsOrganizationModalOpen(false);
    dispatch(setSelectedOrganization(null));
  };

  // 画像スタイルモーダルを開く関数
  const openImageStyleModal = (imageStyle?: AdminImageStyle) => {
    if (imageStyle) {
      dispatch(setSelectedImageStyle(imageStyle));
    } else {
      dispatch(setSelectedImageStyle(null));
    }
    setIsImageStyleModalOpen(true);
  };

  // 画像スタイルモーダルを閉じる関数
  const closeImageStyleModal = () => {
    setIsImageStyleModalOpen(false);
    dispatch(setSelectedImageStyle(null));
  };

  // エージェント保存処理
  const handleAgentSubmit = async (agentData: any) => {
    try {
      if (selectedAgent) {
        // 既存エージェントの更新
        await dispatch(updateAgent({ id: selectedAgent.id, agentData })).unwrap();
      } else {
        // 新規エージェントの作成
        await dispatch(createAgent(agentData)).unwrap();
      }
      closeAgentModal();
    } catch (error) {
      console.error('Failed to save agent:', error);
    }
  };

  // ユーザー保存処理
  const handleUserSubmit = async (userData: any) => {
    try {
      // デバッグ：送信するデータをログに出力
      console.log('送信するuserData:', userData);
      console.log('パスワードが含まれているか:', 'password' in userData);
      if (userData.password) {
        console.log('パスワードの長さ:', userData.password.length);
      }
      
      let result;
      if (selectedUser) {
        // 既存ユーザーの更新
        console.log('ユーザー更新:', selectedUser.id);
        result = await dispatch(updateUser({ id: selectedUser.id, userData })).unwrap();
      } else {
        // 新規ユーザーの作成
        console.log('新規ユーザー作成');
        result = await dispatch(createUser(userData)).unwrap();
      }
      
      console.log('ユーザー保存結果:', result);
      
      // Firebase自動作成が必要な場合の処理
      const creationInfo = result?.firebase_creation_info as
        | AdminFirebaseCreationInfo
        | undefined;

      if (result && result.firebase_account_creation_required && creationInfo) {
        console.log('Firebase自動作成が必要です:', {
          user_id: creationInfo.user_id,
          email: creationInfo.email,
          password_available: creationInfo.password_available,
        });

        // 平文の初期パスワードが無い場合は Firebase 作成を試みない。
        // 以前 backend は password が無いとき 'TempPassword123!' を返しており、
        // 全ユーザー共通の既知パスワードで Firebase アカウントが作られていた。
        // 現在は password: null / password_available: false が返るので、
        // 管理者にパスワード設定リンク運用へ誘導する。
        const initialPassword =
          creationInfo.password_available === false ? null : creationInfo.password;

        if (!initialPassword) {
          await confirm({
            title: `ユーザー「${creationInfo.name}」を保存しましたが、初期パスワードが無いため Firebase アカウントは作成していません。`,
            description:
              `メールアドレス: ${creationInfo.email}\n\n` +
              'パスワード設定リンク（または再設定）で本人にパスワードを設定してもらってください。',
            tone: 'info',
            confirmText: 'OK',
            hideCancel: true,
          });
        } else {
          try {
            // firebaseServiceを動的にインポート
            const { default: firebaseService } = await import('../../services/firebase/firebaseService');

            // Firebaseアカウントを作成
            const firebaseResult = await firebaseService.registerWithEmailAndPassword(
              creationInfo.email,
              initialPassword,
              creationInfo.name
            );

            console.log('Firebase自動作成成功:', firebaseResult);

            // 作成されたFirebase UIDをバックエンドに送信して更新
            if (firebaseResult && firebaseResult.user && firebaseResult.user.firebase_uid) {
              const updateData = {
                firebase_uid: firebaseResult.user.firebase_uid,
                firebase_email: creationInfo.email
              };

              await dispatch(updateUser({
                id: creationInfo.user_id,
                userData: updateData
              })).unwrap();

              console.log('Firebase UID をデータベースに保存しました');

              // 成功メッセージを表示
              await confirm({
                title: `ユーザー「${creationInfo.name}」のFirebaseアカウントが自動作成されました。`,
                description: `メールアドレス: ${creationInfo.email}\nパスワード: ${initialPassword}\n\nこの情報をユーザーに伝えてください。`,
                tone: 'info',
                confirmText: 'OK',
                hideCancel: true,
              });
            }

          } catch (firebaseError) {
            console.error('Firebase自動作成に失敗:', firebaseError);
            await confirm({
              title: 'ユーザー情報は更新されましたが、Firebaseアカウントの自動作成に失敗しました。',
              description: `手動でFirebaseアカウントを作成してください：\nメールアドレス: ${creationInfo.email}\nパスワード: ${initialPassword}`,
              tone: 'info',
              confirmText: 'OK',
              hideCancel: true,
            });
          }
        }
      }

      closeUserModal();
    } catch (error) {
      console.error('Failed to save user:', error);
    }
  };

  // 組織保存処理
  const handleOrganizationSubmit = async (organizationData: Partial<Organization>) => {
    try {
      if (selectedOrganization) {
        // 既存組織の更新
        await dispatch(updateOrganization({ 
          id: selectedOrganization.id, 
          data: organizationData 
        })).unwrap();
      } else {
        // 新規組織の作成
        const createData = {
          name: organizationData.name || '',
          description: organizationData.description,
          member_limit: organizationData.member_limit || 100,
          monthly_token_limit: organizationData.monthly_token_limit || 1000000,
          max_concurrent_image_jobs: organizationData.max_concurrent_image_jobs ?? 20
        };
        await dispatch(createOrganization(createData)).unwrap();
      }
      closeOrganizationModal();
    } catch (error) {
      console.error('Failed to save organization:', error);
    }
  };

  // ロゴ更新後のコールバック
  const handleLogoUpdate = () => {
    // 組織一覧を再取得
    dispatch(fetchOrganizations());
  };

  // 画像スタイル保存処理
  const handleImageStyleSubmit = async (data: ImageStyleCreateRequest | ImageStyleUpdateRequest) => {
    try {
      if (selectedImageStyle) {
        // 既存の画像スタイルの更新
        await dispatch(updateImageStyle({ id: selectedImageStyle.id, styleData: data as ImageStyleUpdateRequest })).unwrap();
      } else {
        // 新規画像スタイルの作成
        await dispatch(createImageStyle(data as ImageStyleCreateRequest)).unwrap();
      }
      closeImageStyleModal();
    } catch (error) {
      console.error('Failed to save image style:', error);
    }
  };

  // 画像スタイル削除処理
  const handleDeleteImageStyle = async (id: number, name: string) => {
    if (await confirm({ title: `画像スタイル「${name}」を削除してもよろしいですか？`, tone: 'destructive', confirmText: '削除する' })) {
      try {
        await dispatch(deleteImageStyle(id)).unwrap();
      } catch (error) {
        console.error(`Failed to delete image style with ID: ${id}`, error);
        showToast('画像スタイルの削除に失敗しました。', 'error');
      }
    }
  };

  // 画像スタイル有効/無効切り替え
  const handleToggleImageStyle = async (id: number) => {
    try {
      await dispatch(toggleImageStyle(id)).unwrap();
    } catch (error) {
      console.error(`Failed to toggle image style with ID: ${id}`, error);
    }
  };

  // 動画テンプレートモーダルを開く関数
  const openVideoTemplateModal = (template?: AdminVideoTemplate) => {
    if (template) {
      dispatch(setSelectedVideoTemplate(template));
    } else {
      dispatch(setSelectedVideoTemplate(null));
    }
    setIsVideoTemplateModalOpen(true);
  };

  // 動画テンプレートモーダルを閉じる関数
  const closeVideoTemplateModal = () => {
    setIsVideoTemplateModalOpen(false);
    dispatch(setSelectedVideoTemplate(null));
  };

  // 動画テンプレート保存処理
  const handleVideoTemplateSubmit = async (data: VideoTemplateCreateRequest | VideoTemplateUpdateRequest) => {
    try {
      if (selectedVideoTemplate) {
        await dispatch(updateVideoTemplate({ id: selectedVideoTemplate.id, templateData: data as VideoTemplateUpdateRequest })).unwrap();
      } else {
        await dispatch(createVideoTemplate(data as VideoTemplateCreateRequest)).unwrap();
      }
      closeVideoTemplateModal();
    } catch (error) {
      console.error('Failed to save video template:', error);
    }
  };

  // 動画テンプレート削除処理
  const handleDeleteVideoTemplate = async (id: number, name: string) => {
    if (await confirm({ title: `動画テンプレート「${name}」を削除してもよろしいですか？`, tone: 'destructive', confirmText: '削除する' })) {
      try {
        await dispatch(deleteVideoTemplate(id)).unwrap();
      } catch (error) {
        console.error(`Failed to delete video template with ID: ${id}`, error);
        showToast('動画テンプレートの削除に失敗しました。', 'error');
      }
    }
  };

  // 動画テンプレート有効/無効切り替え
  const handleToggleVideoTemplate = async (id: number) => {
    try {
      await dispatch(toggleVideoTemplate(id)).unwrap();
    } catch (error) {
      console.error(`Failed to toggle video template with ID: ${id}`, error);
    }
  };

  // ユーザー削除処理
  const handleDeleteUser = async (userId: number) => {
    if (await confirm({ title: 'このユーザーを削除してもよろしいですか？', tone: 'destructive', confirmText: '削除する' })) {
      try {
        await dispatch(deleteUser(userId)).unwrap();
      } catch (error) {
        console.error(`Failed to delete user with ID: ${userId}`, error);
      }
    }
  };

  // AIエージェント有効/無効切り替え
  const handleToggleAgentStatus = async (agentId: number, currentStatus: boolean) => {
    try {
      const agentData = { is_active: !currentStatus };
      await dispatch(updateAgent({ id: agentId, agentData })).unwrap();
    } catch (error) {
      console.error(`Failed to toggle status for agent with ID: ${agentId}`, error);
    }
  };

  // AIエージェント削除処理
  const handleDeleteAgent = async (agentId: number, agentTitle: string) => {
    if (await confirm({ title: `AIエージェント「${agentTitle}」を削除してもよろしいですか？`, description: '注意: このエージェントがチャットで使用されている場合は削除できません。', tone: 'destructive', confirmText: '削除する' })) {
      try {
        await dispatch(deleteAgent(agentId)).unwrap();
      } catch (error) {
        console.error(`Failed to delete agent with ID: ${agentId}`, error);
        // エラーメッセージを表示（チャットで使用されている場合など）
        showToast('エージェントの削除に失敗しました。このエージェントがチャットで使用されている可能性があります。', 'error');
      }
    }
  };

  // 統計データCSVダウンロード
  const handleDownloadStats = async () => {
    if (!startDate || !endDate) {
      showToast('開始日と終了日を入力してください。', 'error');
      return;
    }

    try {
      setIsDownloading(true);
      
      // APIを呼び出してCSVデータのURLを取得
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/admin/stats/download?start_date=${startDate}&end_date=${endDate}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('統計データのダウンロードに失敗しました');
      }
      
      const data = await response.json();
      
      // ダウンロード用のリンクを作成して自動クリック
      const link = document.createElement('a');
      link.href = data.url;
      link.setAttribute('download', `stats_${startDate}_to_${endDate}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (error) {
      console.error('Failed to download stats CSV:', error);
      showToast('統計データのダウンロードに失敗しました。', 'error');
    } finally {
      setIsDownloading(false);
    }
  };

  const adminName = user?.name || '管理者';
  const desktopSidebar = (
    <AdminSidebar
      activePage={activeTab}
      onPageChange={handleTabChange}
      adminName={adminName}
    />
  );
  const mobileSidebar = (
    <div className="flex min-h-full flex-col bg-dark py-4 text-white">
      <div className="px-5 pb-4 text-lg font-bold">管理パネル</div>
      <nav className="min-h-0 flex-1 overflow-y-auto">
        {ADMIN_TAB_ITEMS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => handleTabChange(key)}
            className={`flex w-full items-center px-5 py-3 text-left text-sm transition-colors ${
              activeTab === key
                ? 'bg-white/[0.12] font-semibold text-white'
                : 'text-white/80 hover:bg-white/[0.08] hover:text-white'
            }`}
          >
            {label}
          </button>
        ))}
      </nav>
      <div className="border-t border-white/10 px-5 pt-4">
        <p className="text-sm font-semibold">{adminName}</p>
        <p className="mt-1 text-xs text-white/60">管理者</p>
      </div>
    </div>
  );

  return (
    <ResponsiveAppShell
      sidebar={desktopSidebar}
      mobileSidebar={mobileSidebar}
      topbarTitle="管理パネル"
      className="bg-warm-bg"
    >
      <div className="flex min-h-[calc(100dvh-56px)] flex-col overflow-hidden bg-warm-bg lg:ml-[250px] lg:h-screen lg:min-h-0">
        {/* ページヘッダー */}
        <header className="hidden h-14 flex-shrink-0 items-center gap-2.5 border-b border-warm-border bg-white px-6 lg:flex">
          <ShieldCheck className="w-5 h-5 text-primary flex-shrink-0" />
          <h1 className="text-lg font-semibold text-dark whitespace-nowrap">管理パネル</h1>
          <p className="hidden md:block text-xs text-gray-400 truncate">ユーザー、AIエージェント、使用統計の管理</p>
        </header>

        {/* タブナビゲーション（Underline Tabs・横スクロール可）。
            PC は左サイドバーに同じ項目が全部あるので出さない（出すと右端で途中から切れる） */}
        <div className="bg-white border-b border-warm-border flex-shrink-0 lg:hidden">
          <div className="flex items-end overflow-x-auto px-3 sm:px-6">
            {ADMIN_TAB_ITEMS.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => handleTabChange(key)}
                className={`px-4 py-2.5 text-[13px] font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === key
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-dark'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <main className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden bg-warm-bg">
          <div className="mx-auto max-w-7xl px-3 py-4 sm:px-6 sm:py-6">

        {/* エラーメッセージ */}
        {error && (
          <TaskyAlert tone="error" className="mb-6">
            {error}
          </TaskyAlert>
        )}

        {/* ローディング中の表示 */}
        {isLoading && (
          <div className="flex items-center gap-3 justify-center py-8">
            <TaskySpinner size="md" />
            <span className="text-sm text-gray-600">データを読み込み中...</span>
          </div>
        )}

        {/* ユーザー管理 */}
        {activeTab === 'users' && !isLoading && (
          <section className="mb-8">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-base font-semibold text-dark">ユーザー一覧</h2>
              <TaskyButton size="sm" onClick={() => openUserModal()}>
                <Plus />
                新規ユーザー追加
              </TaskyButton>
            </div>

            {users.length > 0 ? (
              <TaskyTable className="min-w-[920px]">
                <TaskyTableHead>
                  <tr>
                    <TaskyTableHeaderCell>ID</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>名前</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>メールアドレス</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>プラン</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>オンボーディング</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>組織</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>登録日</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>初回ログイン</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>アクション</TaskyTableHeaderCell>
                  </tr>
                </TaskyTableHead>
                <TaskyTableBody>
                  {users.map(user => (
                    <TaskyTableRow
                      key={user.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/admin/users/${user.id}`)}
                    >
                      <TaskyTableCell className="text-gray-500">{user.id}</TaskyTableCell>
                      <TaskyTableCell>
                        <span className="text-sm font-medium text-dark">{user.name}</span>
                      </TaskyTableCell>
                      <TaskyTableCell className="text-gray-600">{user.email}</TaskyTableCell>
                      <TaskyTableCell>
                        <TaskyBadge variant={!user.organization?.plan ? 'secondary' : 'success'}>
                          {user.organization?.plan === 'personal' ? 'パーソナル' :
                          user.organization?.plan === 'team' ? 'チーム' :
                          user.organization?.plan ? user.organization.plan : '無料'}
                        </TaskyBadge>
                      </TaskyTableCell>
                      <TaskyTableCell>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            dispatch(updateUser({ id: user.id, userData: { onboarding_completed: !user.onboarding_completed } }));
                          }}
                          className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                            user.onboarding_completed
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                          }`}
                        >
                          {user.onboarding_completed ? '完了' : '未完了'}
                        </button>
                      </TaskyTableCell>
                      <TaskyTableCell>
                        {user.organization ? (
                          <span className="text-sm text-gray-600">{user.organization.name}</span>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </TaskyTableCell>
                      <TaskyTableCell className="text-gray-500 whitespace-nowrap">{new Date(user.created_at || '').toLocaleDateString('ja-JP')}</TaskyTableCell>
                      <TaskyTableCell className="text-gray-500 whitespace-nowrap">
                        {user.first_login_at
                          ? new Date(user.first_login_at).toLocaleDateString('ja-JP')
                          : <span className="text-gray-400">-</span>
                        }
                      </TaskyTableCell>
                      <TaskyTableCell>
                        <div className="flex items-center gap-1">
                          <TaskyIconButton
                            type="button"
                            variant="ghost"
                            size="sm"
                            title="編集"
                            aria-label={`${user.name}を編集`}
                            onClick={(e) => {
                              e.stopPropagation();
                              openUserModal(user);
                            }}
                          >
                            <Edit />
                          </TaskyIconButton>
                          <TaskyIconButton
                            type="button"
                            variant="ghost"
                            size="sm"
                            title="削除"
                            aria-label={`${user.name}を削除`}
                            className="hover:text-destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteUser(user.id);
                            }}
                          >
                            <Trash2 />
                          </TaskyIconButton>
                        </div>
                      </TaskyTableCell>
                    </TaskyTableRow>
                  ))}
                </TaskyTableBody>
              </TaskyTable>
            ) : (
              <TaskyEmptyState icon={<Users />} title="ユーザーデータがありません。" className="my-8" />
            )}
          </section>
        )}

        {/* AIエージェント管理 */}
        {activeTab === 'agents' && !isLoading && (
          <section className="mb-8">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-base font-semibold text-dark">AIエージェント一覧</h2>
              <TaskyButton size="sm" onClick={() => openAgentModal()}>
                <Plus />
                新規エージェント追加
              </TaskyButton>
            </div>

            {agents.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4">
                {agents.map(agent => (
                  <div key={agent.id} className="bg-white rounded-tasky-md border border-warm-border p-5 flex flex-col hover:shadow-card transition-shadow">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <h3 className="text-sm font-semibold text-dark">{agent.title}</h3>
                      <TaskyBadge variant={agent.is_active ? 'success' : 'secondary'}>
                        {agent.is_active ? '有効' : '無効'}
                      </TaskyBadge>
                    </div>

                    <p className="text-sm text-gray-600 flex-1 mb-4 whitespace-pre-wrap">
                      {agent.description}
                    </p>

                    <div className="pt-3 border-t border-warm-border space-y-2.5">
                      <p className="text-xs text-gray-400">
                        モデル: <span className="text-dark font-medium">{agent.model_type}</span>
                      </p>

                      <div className="flex flex-wrap items-center gap-1.5">
                        <TaskyButton
                          variant="secondary"
                          size="sm"
                          onClick={() => openAgentModal(agent)}
                        >
                          編集
                        </TaskyButton>
                        <TaskyButton
                          variant={agent.is_active ? 'secondary' : 'default'}
                          size="sm"
                          onClick={() => handleToggleAgentStatus(agent.id, agent.is_active)}
                        >
                          {agent.is_active ? '無効にする' : '有効にする'}
                        </TaskyButton>
                        <TaskyButton
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeleteAgent(agent.id, agent.title)}
                        >
                          削除
                        </TaskyButton>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <TaskyEmptyState icon={<Bot />} title="AIエージェントデータがありません。" className="my-8" />
            )}
          </section>
        )}

        {/* 組織管理 */}
        {activeTab === 'organizations' && !isLoading && (
          <section className="mb-8">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-base font-semibold text-dark">組織一覧</h2>
              <TaskyButton size="sm" onClick={() => openOrganizationModal()}>
                <Plus />
                新規組織追加
              </TaskyButton>
            </div>

            {organizations && organizations.length > 0 ? (
              <TaskyTable className="min-w-[1060px]">
                <TaskyTableHead>
                  <tr>
                    <TaskyTableHeaderCell>ID</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>組織名</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>プラン</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>トライアル</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>ユーザー数</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>月間クレジット上限</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>月間トークン上限</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>残りクレジット</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>状態</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>アクション</TaskyTableHeaderCell>
                  </tr>
                </TaskyTableHead>
                <TaskyTableBody>
                  {organizations.map(org => {
                    const planLabel = org.plan === 'personal' ? 'パーソナル'
                      : org.plan === 'team' ? 'チーム'
                      : org.plan === 'agency' ? 'エージェンシー'
                      : org.plan === 'enterprise' ? 'エンタープライズ'
                      : org.plan === 'demo' ? 'デモ'
                      : org.plan === 'free' ? 'フリー'
                      : org.plan || '—';
                    const trialEnd = (org as any).trial_ends_at as string | null | undefined;
                    const isInTrial = (org as any).is_in_trial as boolean | undefined;
                    let trialLabel: React.ReactNode = <span className="text-gray-400">—</span>;
                    if (isInTrial && trialEnd) {
                      const daysLeft = Math.max(0, Math.ceil((new Date(trialEnd).getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
                      trialLabel = <span className="text-success font-semibold">残り {daysLeft} 日</span>;
                    } else if (trialEnd && new Date(trialEnd).getTime() < Date.now()) {
                      trialLabel = <span className="text-destructive">終了</span>;
                    }
                    return (
                    <TaskyTableRow key={org.id} className="cursor-pointer" onClick={() => {
                      dispatch(setSelectedOrganization(org));
                      navigate(`/admin/organizations/${org.id}`);
                    }}>
                      <TaskyTableCell className="text-gray-500">{org.id}</TaskyTableCell>
                      <TaskyTableCell>
                        <span className="text-sm font-medium text-dark">{org.name}</span>
                      </TaskyTableCell>
                      <TaskyTableCell>
                        {org.plan ? (
                          <TaskyBadge variant="warm">{planLabel}</TaskyBadge>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </TaskyTableCell>
                      <TaskyTableCell className="whitespace-nowrap">{trialLabel}</TaskyTableCell>
                      <TaskyTableCell>
                        <div className="text-sm text-dark">{org.total_users ?? ((org.leader_count || 0) + (org.member_count || 0))}</div>
                        <div className="text-xs text-gray-500">
                          リーダー {org.leader_count || 0} / メンバー {org.member_count || 0}
                        </div>
                        <div className="text-xs text-gray-400">
                          メンバー上限 {formatLimit(org.member_limit)}
                        </div>
                      </TaskyTableCell>
                      <TaskyTableCell className="whitespace-nowrap">{Math.round(org.monthly_credit_limit || 0).toLocaleString()}</TaskyTableCell>
                      <TaskyTableCell className="whitespace-nowrap">{Math.round(org.monthly_token_limit || 0).toLocaleString()}</TaskyTableCell>
                      <TaskyTableCell className="whitespace-nowrap">{Math.round(org.remaining_credits ?? org.remaining_tokens ?? 0).toLocaleString()}</TaskyTableCell>
                      <TaskyTableCell>
                        <TaskyBadge variant={org.is_active ? 'success' : 'secondary'}>
                          {org.is_active ? '有効' : '無効'}
                        </TaskyBadge>
                      </TaskyTableCell>
                      <TaskyTableCell>
                        <div className="flex items-center gap-1">
                          <TaskyIconButton
                            type="button"
                            variant="ghost"
                            size="sm"
                            title="編集"
                            aria-label={`${org.name}を編集`}
                            onClick={(e) => {
                              e.stopPropagation();
                              openOrganizationModal(org);
                            }}
                          >
                            <Edit />
                          </TaskyIconButton>
                          <TaskyIconButton
                            type="button"
                            variant="ghost"
                            size="sm"
                            title={org.is_active ? '無効にする' : '有効にする'}
                            aria-label={`${org.name}を${org.is_active ? '無効にする' : '有効にする'}`}
                            className={org.is_active ? 'hover:text-destructive' : 'hover:text-success'}
                            onClick={async (e) => {
                              e.stopPropagation();
                              const action = org.is_active ? '無効化' : '有効化';
                              if (await confirm({ title: `組織「${org.name}」を${action}してもよろしいですか？`, description: org.is_active ? '無効化すると、所属ユーザーはログインできなくなります。' : undefined, tone: org.is_active ? 'destructive' : 'warning', confirmText: action })) {
                                dispatch(
                                  toggleOrganizationActive({
                                    id: org.id,
                                    isActive: !org.is_active
                                  })
                                );
                              }
                            }}
                          >
                            <Power />
                          </TaskyIconButton>
                          <TaskyIconButton
                            type="button"
                            variant="ghost"
                            size="sm"
                            title="削除"
                            aria-label={`${org.name}を削除`}
                            className="hover:text-destructive"
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (await confirm({ title: `組織「${org.name}」を削除してもよろしいですか？`, description: '※所属メンバーも一緒に削除されます。', tone: 'destructive', confirmText: '削除する' })) {
                                dispatch(deleteOrganization(org.id));
                              }
                            }}
                          >
                            <Trash2 />
                          </TaskyIconButton>
                        </div>
                      </TaskyTableCell>
                    </TaskyTableRow>
                    );
                  })}
                </TaskyTableBody>
              </TaskyTable>
            ) : (
              <TaskyEmptyState icon={<Building2 />} title="組織データがありません。" className="my-8" />
            )}
          </section>
        )}

        {/* カテゴリ管理 */}
        {activeTab === 'categories' && (
          <div className="mb-8 overflow-x-auto rounded-tasky-md border border-warm-border bg-white p-4 sm:p-6">
            <CategoryList />
          </div>
        )}

        {/* 画像スタイル管理 */}
        {activeTab === 'imageStyles' && !isLoading && (
          <section className="mb-8">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-base font-semibold text-dark">画像スタイル一覧</h2>
              <TaskyButton size="sm" onClick={() => openImageStyleModal()}>
                <Plus />
                新規スタイル追加
              </TaskyButton>
            </div>

            {imageStyles.length > 0 ? (
              <TaskyTable className="min-w-[760px]">
                <TaskyTableHead>
                  <tr>
                    <TaskyTableHeaderCell>ID</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>カテゴリ</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>スタイル名</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>説明</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>並び順</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>状態</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>アクション</TaskyTableHeaderCell>
                  </tr>
                </TaskyTableHead>
                <TaskyTableBody>
                  {imageStyles.map(style => (
                    <TaskyTableRow key={style.id}>
                      {/* 3桁の ID が「16／3」と割れないよう折り返さない */}
                      <TaskyTableCell className="text-gray-500 whitespace-nowrap">{style.id}</TaskyTableCell>
                      <TaskyTableCell>
                        <TaskyBadge variant="warm">{style.category}</TaskyBadge>
                      </TaskyTableCell>
                      <TaskyTableCell>
                        <span className="text-sm font-medium text-dark">{style.name}</span>
                      </TaskyTableCell>
                      <TaskyTableCell className="text-gray-600">{style.description ? (style.description.length > 50 ? style.description.substring(0, 50) + '...' : style.description) : '-'}</TaskyTableCell>
                      <TaskyTableCell className="text-gray-500">{style.sort_order}</TaskyTableCell>
                      <TaskyTableCell>
                        <TaskyBadge variant={style.is_active ? 'success' : 'secondary'}>
                          {style.is_active ? '有効' : '無効'}
                        </TaskyBadge>
                      </TaskyTableCell>
                      <TaskyTableCell>
                        <div className="flex items-center gap-1">
                          <TaskyIconButton
                            type="button"
                            variant="ghost"
                            size="sm"
                            title="編集"
                            aria-label={`${style.name}を編集`}
                            onClick={() => openImageStyleModal(style)}
                          >
                            <Edit />
                          </TaskyIconButton>
                          <TaskyIconButton
                            type="button"
                            variant="ghost"
                            size="sm"
                            title={style.is_active ? '無効にする' : '有効にする'}
                            aria-label={`${style.name}を${style.is_active ? '無効にする' : '有効にする'}`}
                            className={style.is_active ? 'hover:text-destructive' : 'hover:text-success'}
                            onClick={() => handleToggleImageStyle(style.id)}
                          >
                            <Power />
                          </TaskyIconButton>
                          <TaskyIconButton
                            type="button"
                            variant="ghost"
                            size="sm"
                            title="削除"
                            aria-label={`${style.name}を削除`}
                            className="hover:text-destructive"
                            onClick={() => handleDeleteImageStyle(style.id, style.name)}
                          >
                            <Trash2 />
                          </TaskyIconButton>
                        </div>
                      </TaskyTableCell>
                    </TaskyTableRow>
                  ))}
                </TaskyTableBody>
              </TaskyTable>
            ) : (
              <TaskyEmptyState icon={<Palette />} title="画像スタイルデータがありません。" className="my-8" />
            )}
          </section>
        )}

        {/* 画像スタイルカテゴリ管理 */}
        {/* スタイル品質（生成後の反応と Clef 判定による採点・非表示候補） */}
        {activeTab === 'styleQuality' && <StyleQualityPanel />}

        {activeTab === 'imageStyleCategories' && (
          <div className="mb-8 overflow-x-auto rounded-tasky-md border border-warm-border bg-white p-4 sm:p-6">
            <ImageStyleCategoryList />
          </div>
        )}

        {/* 素材スタイル管理 */}
        {activeTab === 'materialStyles' && (
          <div className="mb-8 overflow-x-auto rounded-tasky-md border border-warm-border bg-white p-4 sm:p-6">
            <MaterialStyleList />
          </div>
        )}

        {/* 素材スタイルカテゴリ管理 */}
        {activeTab === 'materialStyleCategories' && (
          <div className="mb-8 overflow-x-auto rounded-tasky-md border border-warm-border bg-white p-4 sm:p-6">
            <MaterialStyleCategoryList />
          </div>
        )}

        {/* カルーセルスタイル管理 */}
        {activeTab === 'carouselStyles' && (
          <div className="mb-8 overflow-x-auto rounded-tasky-md border border-warm-border bg-white p-4 sm:p-6">
            <CarouselStyleManagement />
          </div>
        )}

        {/* プロンプトテンプレート管理 */}
        {activeTab === 'promptTemplates' && (
          <div className="mb-8 overflow-x-auto rounded-tasky-md border border-warm-border bg-white p-4 sm:p-6">
            <ImagePromptTemplateList />
          </div>
        )}

        {/* バナープロンプト週次分析 */}
        {activeTab === 'bannerPromptReports' && (
          <div className="mb-8 overflow-x-auto rounded-tasky-md border border-warm-border bg-white p-4 sm:p-6">
            <BannerPromptWeeklyReports />
          </div>
        )}

        {/* サンプルプロンプト管理 */}
        {activeTab === 'samplePrompts' && (
          <div className="mb-8 overflow-x-auto rounded-tasky-md border border-warm-border bg-white p-4 sm:p-6">
            <SamplePromptList />
          </div>
        )}

        {/* 漫画スタイル管理 */}
        {activeTab === 'mangaStyles' && (
          <div className="mb-8 overflow-x-auto rounded-tasky-md border border-warm-border bg-white p-4 sm:p-6">
            <MangaStyleManagement />
          </div>
        )}

        {/* 音楽スタイル管理 */}
        {activeTab === 'musicStyles' && (
          <div className="mb-8 overflow-x-auto rounded-tasky-md border border-warm-border bg-white p-4 sm:p-6">
            <MusicStyleManagement />
          </div>
        )}

        {/* 動画テンプレート管理 */}
        {activeTab === 'videoTemplates' && !isLoading && (
          <section className="mb-8">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-base font-semibold text-dark">動画テンプレート一覧</h2>
              <TaskyButton size="sm" onClick={() => openVideoTemplateModal()}>
                <Plus />
                新規テンプレート追加
              </TaskyButton>
            </div>

            <div className="overflow-x-auto rounded-tasky-md border border-warm-border bg-white p-4 sm:p-6">
              <VideoTemplateList
                templates={videoTemplates}
                onEdit={openVideoTemplateModal}
                onDelete={handleDeleteVideoTemplate}
                onToggle={handleToggleVideoTemplate}
              />
            </div>
          </section>
        )}

        {/* マジックリサイズプリセット管理 */}
        {activeTab === 'magicResizePresets' && (
          <div className="mb-8 overflow-x-auto rounded-tasky-md border border-warm-border bg-white p-4 sm:p-6">
            <MagicResizePresetManager />
          </div>
        )}

        {/* マジックリサイズデバッグ */}
        {activeTab === 'magicResizeDebug' && (
          <MagicResizeDebugPage />
        )}

        {/* ニュース管理 */}
        {activeTab === 'news' && (
          <div className="mb-8 overflow-x-auto rounded-tasky-md border border-warm-border bg-white p-4 sm:p-6">
            <NewsManagementSection />
          </div>
        )}

        {/* ヘルプ記事管理（広告自動化マンのRAG的な情報源。利用者には見せない） */}
        {activeTab === 'helpArticles' && (
          <div className="mb-8 overflow-x-auto rounded-tasky-md border border-warm-border bg-white p-4 sm:p-6">
            <HelpArticleManagementSection />
          </div>
        )}

        {/* 外部連携 API トークン管理 */}
        {activeTab === 'externalApiTokens' && (
          <div className="mb-8 overflow-x-auto rounded-tasky-md border border-warm-border bg-white p-4 sm:p-6">
            <ExternalApiTokenList />
          </div>
        )}

        {/* BigQuery BI連携 */}
        {activeTab === 'bigQueryHandoff' && (
          <BigQueryHandoffPanel />
        )}

        {/* UGC 風動画の出演者（顔画像）・場所（写真） */}
        {activeTab === 'ugcPresets' && <UgcPresetPanel />}

        {/* 動画スタイルレシピ（参考動画の分解・公開の切り替え） */}
        {activeTab === 'videoStyleRecipes' && <VideoStyleRecipePanel />}

        {/* チュートリアル（ページ別ガイドの表示ON/OFFと既読リセット） */}
        {activeTab === 'tutorials' && (
          <TutorialSettingsPanel />
        )}

        {/* AI解析モデル設定（生成パイプラインのテキスト・画像解析モデル） */}
        {activeTab === 'aiAnalysisModels' && (
          <AiAnalysisModelPanel />
        )}

        {activeTab === 'adAccounts' && (
          <AdAccountOverviewPanel />
        )}

        {/* 行動分析（全画面の表示・クリック・ファネル・セッション） */}
        {activeTab === 'behavior' && (
          <BehaviorAnalyticsPanel />
        )}

        {/* パートナー管理 */}
        {activeTab === 'partnerPortal' && (
          <div className="mb-8 overflow-x-auto rounded-tasky-md border border-warm-border bg-white p-4 sm:p-6">
            <PartnerPortalManagement />
          </div>
        )}

        {/* キャンペーン（消費クレジットの期間限定割引） */}
        {activeTab === 'campaigns' && (
          <CampaignManagementPanel />
        )}

        {/* 使用統計 */}
        {activeTab === 'statistics' && (
          <>
            {/* AI使用量ダッシュボード */}
            <section className="mb-8">
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-base font-semibold text-dark">AI使用量概要</h2>
              </div>
              <div className="overflow-x-auto rounded-tasky-md border border-warm-border bg-white p-4 sm:p-6">
                <UsageOverviewDashboard />
              </div>
            </section>

            {/* 従来の統計情報 */}
            {stats && (
              <>
                <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                  <TaskyStatCard
                    label="総ユーザー数"
                    value={stats.totalUsers}
                    icon={<Users />}
                    iconTone="primary"
                  />
                  <TaskyStatCard
                    label="アクティブユーザー"
                    value={stats.activeUsers}
                    icon={<UserCheck />}
                    iconTone="success"
                  />
                  <TaskyStatCard
                    label="総チャット数"
                    value={stats.totalChats.toLocaleString()}
                    icon={<MessageSquare />}
                    iconTone="purple"
                  />
                  <TaskyStatCard
                    label="総メッセージ数"
                    value={stats.totalMessages.toLocaleString()}
                    icon={<MessagesSquare />}
                    iconTone="warning"
                  />
                </div>

                <section className="mb-8">
                  <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <h2 className="text-base font-semibold text-dark">利用統計ダウンロード</h2>
                  </div>

                  <div className="overflow-x-auto rounded-tasky-md border border-warm-border bg-white p-4 sm:p-6">
                    <p className="text-sm text-gray-600 mb-4">期間を選択して利用統計データをCSV形式でダウンロードできます。</p>

                    <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end">
                      <div>
                        <TaskyLabel htmlFor="admin-stats-start-date">開始日:</TaskyLabel>
                        <TaskyInput
                          id="admin-stats-start-date"
                          type="date"
                          className="w-full sm:w-44"
                          value={startDate || ''}
                          onChange={(e) => setStartDate(e.target.value)}
                        />
                      </div>
                      <div>
                        <TaskyLabel htmlFor="admin-stats-end-date">終了日:</TaskyLabel>
                        <TaskyInput
                          id="admin-stats-end-date"
                          type="date"
                          className="w-full sm:w-44"
                          value={endDate || ''}
                          onChange={(e) => setEndDate(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="mt-4">
                      <TaskyButton
                        onClick={handleDownloadStats}
                        disabled={!startDate || !endDate || isDownloading}
                      >
                        <Download />
                        {isDownloading ? 'ダウンロード中...' : 'CSVダウンロード'}
                      </TaskyButton>
                    </div>
                  </div>
                </section>
              </>
            )}
          </>
        )}
          </div>
        </main>
      </div>

      {/* AIエージェントモーダル */}
      {/* モーダルコンポーネント */}
      <AIAgentFormModal
        isOpen={isAgentModalOpen}
        onClose={closeAgentModal}
        agent={selectedAgent || undefined}
        onSubmit={handleAgentSubmit}
      />
      
      <UserFormModal
        isOpen={isUserModalOpen}
        onClose={closeUserModal}
        user={selectedUser || undefined}
        onSubmit={handleUserSubmit}
        organizations={organizations}
      />

      <OrganizationFormModal
        isOpen={isOrganizationModalOpen}
        onClose={closeOrganizationModal}
        organization={selectedOrganization || undefined}
        onSubmit={handleOrganizationSubmit}
        onLogoUpdate={handleLogoUpdate}
      />

      <ImageStyleFormModal
        isOpen={isImageStyleModalOpen}
        imageStyle={selectedImageStyle}
        existingCategories={imageStyleCategories}
        onSubmit={handleImageStyleSubmit}
        onClose={closeImageStyleModal}
        isLoading={isLoading}
      />

      <VideoTemplateFormModal
        isOpen={isVideoTemplateModalOpen}
        videoTemplate={selectedVideoTemplate}
        onSubmit={handleVideoTemplateSubmit}
        onClose={closeVideoTemplateModal}
        isLoading={isLoading}
      />
    </ResponsiveAppShell>
  );
};

export default AdminPage;
